/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAccountState } from "../../redux/reducers/accountReducer";
import { CaseMilestone, Task, TaskStatus, UserAccount } from "../types";
import { HTTPStatusCode, useApi } from "../api/useApi";
import { useCaseTaskState } from "../../redux/reducers/caseTaskReducer";
import { useCaseTaskActions } from "../../redux/actions/caseTaskActions";
import { useToasts } from "../../components/toasts/useToasts";
import { useCaseMilestoneActions } from "../../redux/actions/caseMilestoneActions";

interface TaskUpsertResult {
    statusCode: number;
    message?: string;
    resource?: Task;
}

export const TaskApiRoutes = {
    getUserTasks: (accountId: string, userId: string, status: string = 'Open') => `account/${accountId}/user/${userId}/${status}`,
    getNextUserTasks: (accountId: string, userId: string) => `account/${accountId}/user/${userId}/nextTasks`,
    getCaseTasks: (accountId:string, caseId: string, status: string = 'Open') => `account/${accountId}/case/${caseId}/${status}`,
    getUserCaseTasks: (accountId: string, caseId: string, userId: string, status: string = 'Open') => `account/${accountId}/case/${caseId}/user/${userId}/${status}`,
    upsertTask: (accountId: string) => `account/${accountId}`,
    upsertTasks: (accountId: string) => `account/${accountId}/tasks`,
    completeTask: (accountId: string, caseId:string, taskId: string) => `account/${accountId}/case/${caseId}/task/${taskId}/complete`,
    closeTask: (accountId: string,caseId: string, taskId: string) => `account/${accountId}/case/${caseId}/task/${taskId}/close`,
    reopenTask: (accountId: string, caseId:string, taskId: string) => `account/${accountId}/case/${caseId}/task/${taskId}/reopen`,
    deleteTask: (accountId: string,caseId: string, taskId: string) => `account/${accountId}/case/${caseId}/task/${taskId}`,
}

export type CaseTaskService = {
    tasks: Task[];
    tasksByStatus: (status: TaskStatus) => Task[];
    loadUserTasksAsync: (status?: string) => Promise<void>;
    loadNextUserTasksAsync: () => Promise<void>;
    loadCaseTasksAsync: (caseId: string, status?: string) => Promise<void>;
    loadUserTasksForCaseAsync: (caseId: string, status?: string) => Promise<void>;
    upsertTaskAsync: (task: Task) => Promise<void>;
    upsertTasksAsync: (tasks: Task[]) => Promise<void>;
    onClose: (task: Task) => Promise<void>;
    onComplete: (task: Task) => Promise<void>;
    onReopen: (task: Task) => Promise<void>;
    deleteTaskAsync: (task: Task) => Promise<void>;
}
/**
 * This service will load and cache tasks for a specific case or user,
 * and provides methods for interacting with them
 * @param caseId
 * @returns
 */
export const useTaskService = (): CaseTaskService => {

    //Hooks
    const { primaryAccount } = useAccountState();
    const { account, user } = primaryAccount as UserAccount ?? {};
    const caseTaskState = useCaseTaskState();
    const caseTaskActions = useCaseTaskActions();
    const milestoneActions = useCaseMilestoneActions();
    const toasts = useToasts();

    //Api
    const api = useApi<Task>(`api/Task`);

    //Data Calls
    const loadUserTasksAsync = async (status: string = 'Open') => {
        caseTaskActions.setCaseTasks([]);
        const tasks = await api.getAsync(TaskApiRoutes.getUserTasks(account?.accountId as string, user?.userId as string, status));
        caseTaskActions.setCaseTasks(tasks || []);
    }
    interface NextTasksResult {
        tasks: Task[];
        milestones: CaseMilestone[];
    }
    const loadNextUserTasksAsync = async () => {
        caseTaskActions.setCaseTasks([]);
        const tasksResult: NextTasksResult = await api.getAsync(
            TaskApiRoutes.getNextUserTasks(account?.accountId as string, user?.userId as string)
        );
        caseTaskActions.setCaseTasks(tasksResult?.tasks || []);
        milestoneActions.setCaseMilestones(tasksResult?.milestones || []);
    };
    const loadCaseTasksAsync = async (caseId: string, status: string = 'All') => {
        caseTaskActions.setCaseTasks([]);
        const tasks = await api.getAsync(TaskApiRoutes.getCaseTasks(account?.accountId as string, caseId, status));
        caseTaskActions.setCaseTasks(tasks || []);
    }
    const loadUserTasksForCaseAsync = async (caseId: string, status: string = 'All') => {
        caseTaskActions.setCaseTasks([]);
        const tasks = await api.getAsync(TaskApiRoutes.getUserCaseTasks(account?.accountId as string, caseId, user?.userId as string, status));
        caseTaskActions.setCaseTasks(tasks || []);
    }

    const upsertTaskAsync = async (task: Task, sendToasts: boolean = true) => {
        caseTaskActions.addOrReplaceCaseTask(task);

        const updatedTaskResult: TaskUpsertResult = await api.postAsync(task, TaskApiRoutes.upsertTask(account?.accountId as string));
        caseTaskActions.addOrReplaceCaseTask(updatedTaskResult.resource as Task);

        if(sendToasts) {
            if(updatedTaskResult.statusCode == HTTPStatusCode.created) {
                toasts.tasks.notifyTaskCreated(updatedTaskResult.resource as Task);
            } else if(updatedTaskResult.statusCode == HTTPStatusCode.updated) {
                toasts.tasks.notifyTaskUpdated(updatedTaskResult.resource as Task);
            }
        }
    }
    const upsertTasksAsync = async (tasks: Task[]) => {
        
        try{
            caseTaskActions.addOrReplaceCaseTasks(tasks);
            const updatedTaskResults: TaskUpsertResult[] = await api.postAsync(tasks, TaskApiRoutes.upsertTasks(account?.accountId as string));
            
            caseTaskActions.addOrReplaceCaseTasks(updatedTaskResults.map(r => r.resource as Task).filter(t => t !== undefined));
            
            const createdTasks = updatedTaskResults.filter(r => r.statusCode == HTTPStatusCode.created);
            const updatedTasks = updatedTaskResults.filter(r => r.statusCode == HTTPStatusCode.updated);
            const createdText = createdTasks.length > 0 ? `${createdTasks.length} task(s) created` : '';
            const updatedText = updatedTasks.length > 0 ? `${updatedTasks.length} task(s) updated` : '';
            toasts.notifySuccess('Tasks created/updated',`${createdText}${createdText && updatedText ? ', ' : ''}${updatedText}`);
        }catch(e: any) {
            toasts.notifyError('Error updating tasks', e.message);
            return;
        }
    };
    const completeTaskAsync = async (task: Task) => {
        caseTaskActions.addOrReplaceCaseTask(task);
        const updatedTask = await api.putAsync(task, TaskApiRoutes.completeTask(account?.accountId as string, task.caseId as string, task.taskId as string));
        caseTaskActions.addOrReplaceCaseTask(updatedTask);
    }
    const closeTaskAsync = async (task: Task) => {
        caseTaskActions.addOrReplaceCaseTask(task);
        const updatedTask = await api.putAsync(task, TaskApiRoutes.closeTask(account?.accountId as string, task.caseId as string, task.taskId as string));
        caseTaskActions.addOrReplaceCaseTask(updatedTask);
    }
    const reopenTaskAsync = async (task: Task) => {
        caseTaskActions.addOrReplaceCaseTask(task);
        const updatedTask = await api.putAsync(task, TaskApiRoutes.reopenTask(account?.accountId as string, task.caseId as string, task.taskId as string));
        caseTaskActions.addOrReplaceCaseTask(updatedTask);
    }
    const deleteTaskAsync = async (task: Task) => {
        caseTaskActions.removeCaseTask(task.taskId as string);
        await api.deleteAsync([task.taskId as string], TaskApiRoutes.deleteTask(account?.accountId as string, task.caseId as string, task.taskId as string));
    }

    //Sorting and Filtering
    const sortTasks = (a: Task, b: Task) => {
        return new Date(b.lifecycle?.createdOn as Date).getTime() - new Date(a.lifecycle?.createdOn as Date).getTime();
    }
    
    //Logic
    const onComplete = async (task: Task) => {
        const newTask = {...task, status: TaskStatus.completed}; //leave this here
        await completeTaskAsync(newTask);
    }
    const onClose = async (task: Task) => {
        const newTask = {...task, status: TaskStatus.closed}; //leave this here
        await closeTaskAsync(newTask);
    }    
    const onReopen = async (task: Task) => {
        const newTask = {...task, status: TaskStatus.open}; //leave this here
        await reopenTaskAsync(newTask);
    }    

    return {
        tasks: [...(caseTaskState.tasks || [])].sort(sortTasks),
        tasksByStatus: (status: TaskStatus) => (caseTaskState.tasks || []).filter((t: Task) => t.status === status),
        loadUserTasksAsync,
        loadNextUserTasksAsync,
        loadCaseTasksAsync,
        loadUserTasksForCaseAsync,
        upsertTaskAsync,
        upsertTasksAsync,
        onClose,
        onComplete,
        onReopen,
        deleteTaskAsync
    } as CaseTaskService;
}